import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import type { ChatStatus } from '../../types';

interface ChatStatusIndicatorProps {
  status: ChatStatus;
  unreadCount: number;
  onClick: () => void;
  isMinimized?: boolean;
  className?: string;
  'data-testid'?: string;
}

export default function ChatStatusIndicator({
  status = 'inactive',
  unreadCount = 0,
  onClick,
  isMinimized = false,
  className = '',
  'data-testid': dataTestId
}: ChatStatusIndicatorProps): JSX.Element {
  
  // Ensure unreadCount is a valid number
  const safeUnreadCount = Math.max(0, Math.floor(unreadCount || 0));
  
  // Determine base circle styling based on status
  const getBaseCircleStyle = () => {
    switch (status) {
      case 'active':
        return 'bg-primary-600 hover:bg-primary-700';
      case 'new_messages':
        return 'bg-primary-600 hover:bg-primary-700';
      case 'inactive':
      default:
        return 'bg-primary-600 hover:bg-primary-700';
    }
  };

  // Determine if we should show notification dot
  const shouldShowNotification = status === 'new_messages' && safeUnreadCount > 0;

  // Determine notification dot styling
  const getNotificationDotStyle = () => {
    if (safeUnreadCount > 0) {
      return 'bg-red-500 border-2 border-white dark:border-gray-800';
    }
    return 'bg-green-500 border-2 border-white dark:border-gray-800';
  };

  // Animation classes based on status
  const getAnimationClasses = () => {
    if (status === 'new_messages') {
      return 'animate-pulse';
    }
    if (status === 'inactive') {
      return 'animate-bounce-gentle';
    }
    return '';
  };

  // Generate accessible label
  const getAriaLabel = () => {
    if (isMinimized) {
      return "Restore chat";
    }
    if (status === 'new_messages' && safeUnreadCount > 0) {
      return `Open chat (${safeUnreadCount} new message${safeUnreadCount === 1 ? '' : 's'})`;
    }
    return "Open chat";
  };

  return (
    <button
      onClick={onClick}
      className={`
        fixed bottom-6 right-6 z-50 
        ${getBaseCircleStyle()} 
        text-white p-4 rounded-full shadow-lg 
        transition-all duration-300 hover:scale-105 
        ${getAnimationClasses()}
        ${className}
      `}
      aria-label={getAriaLabel()}
      data-testid={dataTestId}
    >
      {/* Main Chat Icon */}
      <ChatBubbleLeftRightIcon className="h-6 w-6" />
      
      {/* Status Indicator Overlay */}
      {shouldShowNotification && (
        <div className="absolute -top-1 -right-1 flex items-center justify-center">
          {/* Notification Dot/Badge */}
          {safeUnreadCount > 0 && safeUnreadCount <= 9 ? (
            // Small number badge for 1-9 messages
            <div className={`
              ${getNotificationDotStyle()}
              w-5 h-5 rounded-full flex items-center justify-center
              text-xs font-bold text-white
            `}>
              {safeUnreadCount}
            </div>
          ) : safeUnreadCount > 9 ? (
            // "9+" badge for more than 9 messages
            <div className={`
              ${getNotificationDotStyle()}
              w-6 h-5 rounded-full flex items-center justify-center
              text-xs font-bold text-white
            `}>
              9+
            </div>
          ) : (
            // Simple dot for new messages without count
            <div className={`
              ${getNotificationDotStyle()}
              w-3 h-3 rounded-full
            `} />
          )}
          
          {/* Pulsing ring animation for new messages */}
          {status === 'new_messages' && (
            <div className="absolute inset-0 rounded-full border-2 border-red-400 animate-ping opacity-75" />
          )}
        </div>
      )}
      
      {/* Active Session Indicator (subtle blue glow when active but no new messages) */}
      {status === 'active' && !shouldShowNotification && (
        <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-blue-400 rounded-full border border-white dark:border-gray-800" />
      )}
    </button>
  );
}
